import type { FloorBackgroundCrop } from './types'

/** No crop — full image is the floor (user adjusts in crop dialog if needed). */
export const NO_BLUEPRINT_CROP: FloorBackgroundCrop = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
}

/**
 * Optional preset for Mahageeta-style architectural sheets (legend/title block).
 * Not applied automatically — only offered in the crop dialog.
 */
export const ARCHITECTURAL_BLUEPRINT_CROP: FloorBackgroundCrop = {
  top: 0.01,
  bottom: 0.07,
  left: 0.01,
  right: 0.175,
}

/** Previous auto-applied crop — cleared on floor reload. */
export const LEGACY_BLUEPRINT_CROP: FloorBackgroundCrop = {
  top: 0.04,
  bottom: 0.14,
  left: 0.025,
  right: 0.22,
}

function cropMatches(a: FloorBackgroundCrop | null | undefined, b: FloorBackgroundCrop): boolean {
  if (!a) return false
  return (
    Math.abs((a.top ?? 0) - (b.top ?? 0)) < 0.005 &&
    Math.abs((a.bottom ?? 0) - (b.bottom ?? 0)) < 0.005 &&
    Math.abs((a.left ?? 0) - (b.left ?? 0)) < 0.005 &&
    Math.abs((a.right ?? 0) - (b.right ?? 0)) < 0.005
  )
}

/** @deprecated use isAutoBlueprintCrop */
export function isLegacyBlueprintCrop(crop?: FloorBackgroundCrop | null): boolean {
  return cropMatches(crop, LEGACY_BLUEPRINT_CROP)
}

/** True when crop was auto-applied by older versions (not user-set in crop dialog). */
export function isAutoBlueprintCrop(crop?: FloorBackgroundCrop | null): boolean {
  if (!crop) return false
  if (cropMatches(crop, NO_BLUEPRINT_CROP)) return false
  return cropMatches(crop, ARCHITECTURAL_BLUEPRINT_CROP) || cropMatches(crop, LEGACY_BLUEPRINT_CROP)
}

export function normalizeBlueprintCrop(crop: FloorBackgroundCrop): Required<FloorBackgroundCrop> {
  return {
    top: crop.top ?? 0,
    bottom: crop.bottom ?? 0,
    left: crop.left ?? 0,
    right: crop.right ?? 0,
  }
}

/** Drop zero crop so geometry stores nothing until user crops manually. */
export function effectiveBlueprintCrop(crop: FloorBackgroundCrop): FloorBackgroundCrop | undefined {
  const n = normalizeBlueprintCrop(crop)
  if (n.top === 0 && n.bottom === 0 && n.left === 0 && n.right === 0) return undefined
  return n
}

export type BlueprintAnalysis = {
  imageWidth: number
  imageHeight: number
  /** Aspect ratio of cropped floor content (width / height). */
  contentAspect: number
  crop: FloorBackgroundCrop
  dimensions: { widthM: number; heightM: number } | null
  aspectWarning: string | null
}

/** Parse "120x70", "120 × 70 m", etc. from filenames or floor names. */
export function parseDimensionsFromText(text: string): { widthM: number; heightM: number } | null {
  const m = text.match(/(\d{2,3}(?:\.\d+)?)\s*[x×X*]\s*(\d{2,3}(?:\.\d+)?)\s*(?:m\b)?/i)
  if (!m) return null
  const widthM = Number(m[1])
  const heightM = Number(m[2])
  if (!Number.isFinite(widthM) || !Number.isFinite(heightM) || widthM < 5 || heightM > 500 || heightM < 5) {
    return null
  }
  return { widthM, heightM }
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Invalid image'))
    img.src = src
  })
}

export function analyzeBlueprintImage(
  img: HTMLImageElement,
  hints?: { widthM?: number; heightM?: number; fileName?: string; floorName?: string },
): BlueprintAnalysis {
  const crop = { ...NO_BLUEPRINT_CROP }
  const cw = img.width * (1 - (crop.left ?? 0) - (crop.right ?? 0))
  const ch = img.height * (1 - (crop.top ?? 0) - (crop.bottom ?? 0))
  const contentAspect = cw / Math.max(1, ch)

  let dimensions =
    parseDimensionsFromText(hints?.fileName ?? '') ?? parseDimensionsFromText(hints?.floorName ?? '')
  if (!dimensions && hints?.widthM && hints?.heightM) {
    dimensions = { widthM: hints.widthM, heightM: hints.heightM }
  }

  let aspectWarning: string | null = null
  if (dimensions) {
    const declared = dimensions.widthM / dimensions.heightM
    const relErr = Math.abs(declared - contentAspect) / declared
    if (relErr > 0.1) {
      aspectWarning = `Plan image aspect (${contentAspect.toFixed(2)}) differs from ${dimensions.widthM}×${dimensions.heightM} m (${declared.toFixed(2)}). Verify floor width/height or crop.`
    }
  }

  return {
    imageWidth: img.width,
    imageHeight: img.height,
    contentAspect,
    crop,
    dimensions,
    aspectWarning,
  }
}

async function readAndCompressImage(file: File, maxChars = 2_000_000): Promise<string> {
  if (file.type === 'application/pdf') {
    throw new Error('PDF upload: export the floor plan as PNG or JPG for now (PDF rendering planned)')
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Read failed'))
    reader.onload = () => {
      const img = new Image()
      img.onerror = () => reject(new Error('Invalid image — use PNG, JPG, or WebP'))
      img.onload = () => {
        const maxW = 1600
        const scale = Math.min(1, maxW / img.width)
        const c = document.createElement('canvas')
        c.width = Math.round(img.width * scale)
        c.height = Math.round(img.height * scale)
        const ctx = c.getContext('2d')
        if (!ctx) {
          reject(new Error('Canvas unavailable'))
          return
        }
        ctx.drawImage(img, 0, 0, c.width, c.height)
        let quality = 0.82
        let url = c.toDataURL('image/jpeg', quality)
        while (url.length > maxChars && quality > 0.35) {
          quality -= 0.08
          url = c.toDataURL('image/jpeg', quality)
        }
        if (url.length > maxChars) reject(new Error('Image too large (max ~2MB)'))
        else resolve(url)
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)
  })
}

export async function compressFloorPlanImageFile(file: File): Promise<string> {
  return readAndCompressImage(file)
}

/** Compress image; crop defaults to none (user confirms in crop UI). */
export async function prepareFloorPlanImageUpload(
  file: File,
  hints?: { widthM?: number; heightM?: number; floorName?: string },
): Promise<{ url: string; analysis: BlueprintAnalysis }> {
  const url = await readAndCompressImage(file)
  const img = await loadImage(url)
  const analysis = analyzeBlueprintImage(img, { ...hints, fileName: file.name })
  return { url, analysis }
}

/** @deprecated use prepareFloorPlanImageUpload */
export async function processFloorPlanImageFile(
  file: File,
  hints?: { widthM?: number; heightM?: number; floorName?: string },
): Promise<{ url: string; analysis: BlueprintAnalysis }> {
  return prepareFloorPlanImageUpload(file, hints)
}
